import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import {
  ArrowRight, MessageSquare, Clock, CalendarCheck, Wallet, Boxes, BarChart3,
  ShieldCheck, Slack as SlackIcon, CheckCircle2,
} from 'lucide-react'

const SUPPORT_EMAIL = 'support@managition.com'

/* Slack "brand" mark reused from the auth pages. */
function SlackMark({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
    </svg>
  )
}

const FEATURES = [
  { icon: MessageSquare, title: 'Conversational standups', body: 'The Managition bot DMs each teammate for their daily standup and posts a tidy summary to your channel.' },
  { icon: Clock, title: 'One-tap attendance', body: 'Check in and out from Slack with /in and /out — including work-from-home — and see live timesheets on the web.' },
  { icon: CalendarCheck, title: 'Leave & WFH approvals', body: 'Request leave or WFH with /leaves, route it to the right approver, and track balances automatically.' },
  { icon: Wallet, title: 'Payroll & payslips', body: 'Run monthly payroll from attendance and issue CNIC-locked payslips your team can unlock and download.' },
  { icon: Boxes, title: 'Inventory & assets', body: 'Track company assets with QR tags and assign them to employees.' },
  { icon: BarChart3, title: 'Reports & dashboards', body: 'Attendance, leave and headcount dashboards for managers and admins — always tenant-isolated.' },
]

const SLASH_COMMANDS = [
  { cmd: '/in', desc: 'Check in for the day (add “wfh” to mark working from home)' },
  { cmd: '/out', desc: 'Check out for the day' },
  { cmd: '/wfh', desc: 'Mark yourself as working from home' },
  { cmd: '/standup', desc: 'Start or view your daily standup' },
  { cmd: '/leaves', desc: 'Apply for or review leave requests' },
]

const INSTALL_STEPS = [
  'Register your organization on Managition (or sign in if you already have one).',
  'Open Admin → Settings → Slack.',
  'Click “Connect Slack” and approve the requested permissions in your workspace — this is the “Add to Slack” step.',
  'Invite the Managition bot to the channel where you want standup and attendance summaries posted.',
  'Your team can now use /in, /out, /wfh, /standup and /leaves right inside Slack.',
]

export default function Landing() {
  const { isAuthenticated, org_slug } = useAuthStore()
  const navigate = useNavigate()

  // Signed-in visitors skip the marketing page and go to their workspace.
  useEffect(() => {
    if (isAuthenticated && org_slug) navigate(`/${org_slug}`, { replace: true })
  }, [isAuthenticated, org_slug, navigate])

  const [copied, setCopied] = useState(false)

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* ─── Header ─── */}
      <header className="sticky top-0 z-20 border-b border-gray-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <img src="/logo-dark.png" alt="Managition" className="h-8" />
          <nav className="flex items-center gap-2 sm:gap-4">
            <a href="#slack" className="hidden text-sm font-medium text-gray-600 hover:text-gray-900 sm:inline">How it works</a>
            <a href="#install" className="hidden text-sm font-medium text-gray-600 hover:text-gray-900 sm:inline">Install</a>
            <Link to="/support" className="hidden text-sm font-medium text-gray-600 hover:text-gray-900 sm:inline">Support</Link>
            <Link to="/register" className="hidden text-sm font-medium text-gray-600 hover:text-gray-900 sm:inline">Register</Link>
            <Link to="/login" className="btn btn-primary btn-sm">Sign in</Link>
          </nav>
        </div>
      </header>

      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-brand/5 to-transparent" />
        <div className="relative mx-auto max-w-6xl px-5 pt-16 pb-14 sm:pt-24 sm:pb-20">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-brand/20 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand-dark">
              <SlackMark className="h-3.5 w-3.5" /> Built for Slack
            </span>
            <h1 className="mt-5 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              HR your team runs <span className="text-brand">inside Slack</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-600">
              Managition is a multi-tenant HR and team-operations platform. It brings daily standups,
              attendance, leave, timesheets and payroll into Slack — so your team gets HR done without
              leaving the conversation, while admins manage everything on the web.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/login" className="btn btn-primary w-full sm:w-auto">
                <SlackMark className="h-5 w-5" /> Sign in with Slack
              </Link>
              <a href="#install" className="btn btn-outline w-full sm:w-auto">
                Add Managition to Slack <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <p className="mt-4 text-xs text-gray-400">
              Each workspace is an isolated tenant. Slack credentials are encrypted at rest.
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-4xl overflow-hidden rounded-2xl border border-gray-100 shadow-card">
            <img src="/attendance.jpg" alt="Managition attendance dashboard" className="w-full" />
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section className="mx-auto max-w-6xl px-5 py-16">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold text-gray-900">Everything HR, in one place</h2>
          <p className="mt-3 text-gray-600">Run the day-to-day from Slack; manage policy, people and payroll on the web.</p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="card p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-semibold text-gray-900">{f.title}</h3>
              <p className="mt-1.5 text-sm text-gray-600">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── How it works with Slack (items 3 & 5) ─── */}
      <section id="slack" className="bg-sidebar text-white">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white">
                <SlackMark className="h-3.5 w-3.5" /> How Managition works with Slack
              </span>
              <h2 className="mt-4 text-3xl font-bold">Your team never leaves Slack</h2>
              <p className="mt-4 text-white/70">
                After an admin connects a workspace, Managition adds a bot user and five slash commands.
                The bot sends daily standup prompts by direct message, posts standup and attendance
                summaries to a channel you choose, and lets everyone log attendance and leave with a
                single command. Managers approve requests and review dashboards on the web app.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-white/80">
                {[
                  'The bot DMs each teammate for their standup and posts a summary to your channel.',
                  'Check-in / check-out and WFH are logged straight from Slack.',
                  'Leave and WFH requests are routed to the right approver with interactive buttons.',
                  'Notifications (reminders, approvals, mentions) arrive in Slack in real time.',
                ].map((t) => (
                  <li key={t} className="flex gap-2.5">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-light" /> {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-white/50">Slash commands</h3>
              <div className="mt-4 divide-y divide-white/10">
                {SLASH_COMMANDS.map((c) => (
                  <div key={c.cmd} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:gap-4">
                    <code className="w-24 shrink-0 rounded-md bg-black/30 px-2 py-1 text-center text-sm font-semibold text-brand-light">{c.cmd}</code>
                    <span className="text-sm text-white/70">{c.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Install instructions (item 2) ─── */}
      <section id="install" className="mx-auto max-w-4xl px-5 py-16">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
            <SlackIcon className="h-6 w-6" />
          </div>
          <h2 className="mt-4 text-3xl font-bold text-gray-900">Add Managition to your Slack</h2>
          <p className="mx-auto mt-3 max-w-xl text-gray-600">
            Installing takes a couple of minutes. The “Add to Slack” approval happens inside your
            organization's admin settings, so you stay in control of who can install it.
          </p>
        </div>

        <ol className="mx-auto mt-10 max-w-2xl space-y-4">
          {INSTALL_STEPS.map((step, i) => (
            <li key={i} className="card flex items-start gap-4 p-5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">{i + 1}</span>
              <p className="pt-1 text-sm text-gray-700">{step}</p>
            </li>
          ))}
        </ol>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/register" className="btn btn-primary">Register your organization</Link>
          <Link to="/login" className="btn btn-outline">Sign in</Link>
        </div>
      </section>

      {/* ─── Trust / privacy strip (item 4 visibility) ─── */}
      <section className="border-y border-gray-100 bg-gray-50">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-5 py-10 text-center sm:flex-row sm:text-left">
          <ShieldCheck className="h-10 w-10 shrink-0 text-brand" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Private by design</h3>
            <p className="mt-1 text-sm text-gray-600">
              We request only the Slack scopes we need to operate, encrypt all Slack credentials at rest,
              and never share data across organizations. Read our{' '}
              <Link to="/privacy" className="font-medium text-brand hover:underline">Privacy Policy</Link>{' '}
              for the details.
            </p>
          </div>
          <button
            onClick={() => { navigator.clipboard?.writeText(SUPPORT_EMAIL); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
            className="btn btn-outline btn-sm shrink-0"
          >
            {copied ? 'Copied!' : SUPPORT_EMAIL}
          </button>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t border-gray-100">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-8 sm:flex-row">
          <img src="/logo-dark.png" alt="Managition" className="h-7" />
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-500">
            <a href="#slack" className="hover:text-gray-900">How it works</a>
            <a href="#install" className="hover:text-gray-900">Install</a>
            <Link to="/login" className="hover:text-gray-900">Sign in</Link>
            <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-900">Terms</Link>
            <Link to="/support" className="hover:text-gray-900">Support</Link>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-gray-900">{SUPPORT_EMAIL}</a>
          </nav>
        </div>
        <p className="pb-8 text-center text-xs text-gray-400">© 2026 Managition. HR that lives in Slack.</p>
      </footer>
    </div>
  )
}
